/**
 * Stammdaten und Einstellungen pflegen.
 *
 * Zwei Dinge, die die Spec ausdruecklich verlangt und die bisher nur der
 * Datenbank offenstanden: Wania pflegt Materialsaetze und Preise selbst, ohne
 * Entwicklereingriff (PV-J02, PV-J07). Und: zentral gepflegte Werte muessen je
 * Projekt ueberschreibbar sein (PV-F02, PV-J08, PV-K09).
 *
 * Beides laeuft ueber dieselbe Oberflaeche. Der Schalter oben entscheidet, ob
 * eine Aenderung fuer alle gilt oder nur fuer dieses eine Projekt.
 *
 * Wer Stammdaten aendern darf, entscheidet der Server anhand der Rolle. Die
 * Oberflaeche versteckt die Knoepfe zwar, aber das ist Hoeflichkeit, keine
 * Sicherung.
 */

import { Fragment, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { vorlageAlsText, vorlageAusText, type Montagesystem } from '@pv/geometrie';

import { db, neueId, speichere } from '../daten/db.ts';
import { tagAnzeige } from '../datum.ts';
import { Offlinenachweis } from './Offlinenachweis.tsx';
import { Benutzerverwaltung } from './Benutzerverwaltung.tsx';
import { Datenblattablage, Datenblattwahl } from './Datenblaetter.tsx';
import { loeseAuf, VORGABEN, type Einstellungsquelle, type Herkunft } from '../einstellungen.ts';
import type {
  Artikel,
  Einstellung,
  Komponente,
  Komponentenart,
  Materialsatz,
  Modul,
  Projekteinstellung,
} from '../typen.ts';

interface Eigenschaften {
  /** Ist ein Projekt offen, laesst sich alles auch nur fuer dieses setzen. */
  projektId: string | null;
  projektname?: string;
  rolle: string;
  onSchliessen: () => void;
}

type Bereich =
  | 'errichter'
  | 'kalkulation'
  | 'wirtschaftlichkeit'
  | 'module'
  | 'komponenten'
  | 'datenblaetter'
  | 'artikel'
  | 'materialsaetze'
  | 'planung'
  | 'offline'
  | 'benutzer';

const BEREICHE: Array<[Bereich, string]> = [
  ['kalkulation', 'Kalkulation'],
  ['wirtschaftlichkeit', 'Wirtschaftlichkeit'],
  ['module', 'Modulpool'],
  ['komponenten', 'Komponenten'],
  ['datenblaetter', 'Datenblätter'],
  ['artikel', 'Artikel und Preise'],
  ['materialsaetze', 'Materialsätze'],
  ['planung', 'Planung und Karte'],
  ['errichter', 'Errichter und Abwicklung'],
  ['offline', 'Offline-Nachweis'],
  ['benutzer', 'Benutzer'],
];

/** Stammdaten gelten immer fuer alle — ein Modulpool je Projekt waere Unsinn. */
// Der Offline-Nachweis gilt fuer genau ein Geraet und wird nicht abgeglichen —
// eine Aussage ueber dieses Tablet ist keine ueber ein anderes.
const NUR_GLOBAL: Bereich[] = [
  'module',
  'komponenten',
  'artikel',
  'materialsaetze',
  'offline',
  'benutzer',
];

const DARF_STAMMDATEN = ['buero', 'admin'];

export function Einstellungen(e: Eigenschaften): React.JSX.Element {
  const [bereich, setBereich] = useState<Bereich>('kalkulation');
  const [jeProjekt, setJeProjekt] = useState(false);

  const global = useLiveQuery(() => db.einstellungen.toArray(), [], [] as Einstellung[]);
  // Ohne offenes Projekt bleibt die Liste leer — die Abfrage laeuft trotzdem,
  // damit die Reihenfolge der Hooks stabil bleibt.
  const projekteinstellungen: Projekteinstellung[] = useLiveQuery(
    () =>
      db.projekteinstellungen
        .where('projekt_id')
        .equals(e.projektId ?? '—')
        .toArray(),
    [e.projektId],
    [] as Projekteinstellung[],
  );

  const darfStammdaten = DARF_STAMMDATEN.includes(e.rolle);
  const zielProjekt = jeProjekt && e.projektId ? e.projektId : null;
  // Bei „Für alle Projekte“ zählt nur der globale Stand. Vorher flossen die
  // Werte des Projekts mit ein — wer dort ein Feld nur antippte, schrieb den
  // Projektwert für alle.
  const quelle: Einstellungsquelle = { global, jeProjekt: zielProjekt ? projekteinstellungen : [] };

  /** Einen Schluessel schreiben — global oder fuer dieses Projekt. */
  async function setze(schluessel: string, eingabe: unknown): Promise<void> {
    let wert = eingabe;
    if (zielProjekt) {
      const vorhanden = projekteinstellungen.find(
        (p) => p.schluessel === schluessel && !p.geloescht_am,
      );
      // Je Projekt nur ablegen, was vom globalen Wert abweicht. Sonst friert
      // ein einziges geändertes Feld alle anderen auf den heutigen Stand ein,
      // und eine spätere globale Preisänderung kommt hier nie an.
      const globalWert = global.find((g) => g.schluessel === schluessel)?.wert;
      if (istObjekt(wert) && istObjekt(globalWert)) {
        const vorher = istObjekt(vorhanden?.wert) ? vorhanden.wert : {};
        wert = Object.fromEntries(
          Object.entries(wert).filter(
            ([k, v]) => k in vorher || JSON.stringify(v) !== JSON.stringify(globalWert[k]),
          ),
        );
      }
      if (vorhanden) {
        await speichere('projekteinstellungen', vorhanden, { wert });
        return;
      }
      const neu: Projekteinstellung = {
        id: neueId(),
        seq: null,
        schmutzig: true,
        geaendert_am: new Date().toISOString(),
        geloescht_am: null,
        projekt_id: zielProjekt,
        schluessel,
        wert,
      };
      await speichere('projekteinstellungen', neu, {
        projekt_id: neu.projekt_id,
        schluessel: neu.schluessel,
        wert: neu.wert,
      });
      return;
    }

    const vorhanden = global.find((g) => g.schluessel === schluessel);
    await speichere('einstellungen', vorhanden ?? { schluessel, wert, seq: null }, { wert });
  }

  /** Eine Projekteinstellung wieder entfernen — dann gilt wieder der globale Wert. */
  async function nimmZurueck(schluessel: string): Promise<void> {
    const vorhanden = projekteinstellungen.find(
      (p) => p.schluessel === schluessel && !p.geloescht_am,
    );
    if (vorhanden) {
      await speichere('projekteinstellungen', vorhanden, {
        geloescht_am: new Date().toISOString(),
      });
    }
  }

  return (
    <div className="druckrahmen" onClick={e.onSchliessen}>
      <div
        className="druckblatt"
        style={{ maxWidth: '58rem' }}
        onClick={(ereignis) => ereignis.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
          <div style={{ flex: 1 }}>
            <p className="marke">Einstellungen</p>
            <h1>Stammdaten und Vorgaben</h1>
          </div>
          <button onClick={e.onSchliessen}>Schließen</button>
        </div>

        {/* --- Global oder je Projekt ----------------------------------- */}
        {e.projektId && !NUR_GLOBAL.includes(bereich) && (
          <div className="knopfreihe">
            <button aria-pressed={!jeProjekt} onClick={() => setJeProjekt(false)}>
              Für alle Projekte
            </button>
            <button aria-pressed={jeProjekt} onClick={() => setJeProjekt(true)}>
              Nur für {e.projektname ?? 'dieses Projekt'}
            </button>
          </div>
        )}

        {jeProjekt && (
          <div className="hinweis">
            Änderungen gelten nur für dieses Projekt. Was nicht überschrieben wird, kommt weiterhin
            aus den globalen Werten.
          </div>
        )}

        {!darfStammdaten && (
          <div className="hinweis">
            {e.rolle === 'monteur'
              ? 'Für die Montage sind die Werte nur lesbar.'
              : 'Globale Werte und Stammdaten sind nur lesbar — je Projekt lässt sich überschreiben.'}{' '}
            Stammdaten und Preise ändern dürfen Büro und Admin — das prüft der Server, nicht diese
            Ansicht.
          </div>
        )}

        <div className="reiter" style={{ marginTop: '1rem' }}>
          {BEREICHE.map(([wert, text]) => (
            <button key={wert} aria-pressed={bereich === wert} onClick={() => setBereich(wert)}>
              {text}
            </button>
          ))}
        </div>

        {/* Beim Wechsel zwischen global und Projekt alles neu aufbauen — die
            Felder halten ihren Wert sonst aus dem vorigen Geltungsbereich. */}
        <div key={zielProjekt ?? 'global'}>
        {bereich === 'kalkulation' && (
          <Kalkulation
            quelle={quelle}
            setze={setze}
            nimmZurueck={nimmZurueck}
            lesbar={darfStammdaten || (!!zielProjekt && e.rolle !== 'monteur')}
          />
        )}
        {bereich === 'wirtschaftlichkeit' && (
          <Wirtschaftlichkeit
            quelle={quelle}
            setze={setze}
            nimmZurueck={nimmZurueck}
            lesbar={darfStammdaten || (!!zielProjekt && e.rolle !== 'monteur')}
          />
        )}
        {bereich === 'planung' && (
          <Planungswerte
            quelle={quelle}
            setze={setze}
            nimmZurueck={nimmZurueck}
            lesbar={darfStammdaten || (!!zielProjekt && e.rolle !== 'monteur')}
          />
        )}
        {bereich === 'errichter' && (
          <>
            <Errichter quelle={quelle} setze={setze} lesbar={darfStammdaten} />
            <Pruefvorlagen quelle={quelle} setze={setze} nimmZurueck={nimmZurueck} lesbar={darfStammdaten || (!!zielProjekt && e.rolle !== 'monteur')} />
            <Erinnerungsfristen quelle={quelle} setze={setze} nimmZurueck={nimmZurueck} lesbar={darfStammdaten || (!!zielProjekt && e.rolle !== 'monteur')} />
          </>
        )}
        {bereich === 'offline' && <Offlinenachweis />}
        {bereich === 'benutzer' && <Benutzerverwaltung rolle={e.rolle} online={navigator.onLine} />}
        {bereich === 'module' && <Modulpool darf={darfStammdaten} />}
        {bereich === 'komponenten' && <Komponentenpool darf={darfStammdaten} />}
        {bereich === 'datenblaetter' && <Datenblattablage darf={darfStammdaten} />}
        {bereich === 'artikel' && (
          <Artikelstamm
            darf={darfStammdaten}
            projektId={zielProjekt}
            quelle={quelle}
            setze={setze}
          />
        )}
        {bereich === 'materialsaetze' && <Materialsaetze darf={darfStammdaten} />}
        </div>
      </div>
    </div>
  );
}

const istObjekt = (wert: unknown): wert is Record<string, unknown> =>
  typeof wert === 'object' && wert !== null && !Array.isArray(wert);

// -----------------------------------------------------------------------------
// Werte mit Herkunft
// -----------------------------------------------------------------------------
interface Werteigenschaften {
  quelle: Einstellungsquelle;
  setze: (schluessel: string, wert: unknown) => Promise<void>;
  nimmZurueck: (schluessel: string) => Promise<void>;
  lesbar: boolean;
}

/** Zeigt, woher ein Wert kommt. Ohne das raet man beim Suchen. */
function Herkunftsmarke({ herkunft }: { herkunft: Herkunft }): React.JSX.Element {
  const text = { projekt: 'nur dieses Projekt', global: 'global', vorgabe: 'Vorgabe' }[herkunft];
  return (
    <span style={{ fontSize: '0.65rem', color: 'var(--grau)', marginLeft: '0.4rem' }}>{text}</span>
  );
}

function Zahlenfeld({
  beschriftung,
  wert,
  einheit,
  schritt = 1,
  onAendern,
  lesbar,
  fehltHinweis,
}: {
  beschriftung: string;
  wert: number | null | undefined;
  einheit?: string;
  schritt?: number;
  onAendern: (wert: number | null) => void;
  lesbar: boolean;
  fehltHinweis?: string;
}): React.JSX.Element {
  return (
    <label style={{ fontSize: '0.7rem', color: 'var(--grau)', display: 'block' }}>
      {beschriftung} {einheit && <span>in {einheit}</span>}
      <input
        key={String(wert ?? '')}
        type="number"
        step={schritt}
        defaultValue={wert ?? ''}
        disabled={!lesbar}
        onBlur={(ereignis) => {
          const neu = ereignis.target.value === '' ? null : Number(ereignis.target.value);
          if (neu !== null && !Number.isFinite(neu)) return;
          // Nur Durchtippen schreibt nichts.
          if (neu !== (wert ?? null)) onAendern(neu);
        }}
      />
      {wert == null && fehltHinweis && (
        <span style={{ display: 'block', color: 'var(--waerme)', fontSize: '0.7rem' }}>
          {fehltHinweis}
        </span>
      )}
    </label>
  );
}

function Kalkulation({ quelle, setze, nimmZurueck, lesbar }: Werteigenschaften): React.JSX.Element {
  const { wert, herkunft } = loeseAuf(
    quelle,
    'kalkulation.standard',
    VORGABEN['kalkulation.standard'],
  );
  const aendere = (feld: string, neu: unknown): void =>
    void setze('kalkulation.standard', { ...wert, [feld]: neu });

  const warengruppen = [
    ['module', 'Module'],
    ['wechselrichter', 'Wechselrichter'],
    ['speicher', 'Speicher'],
    ['montagematerial', 'Montagematerial'],
    ['elektromaterial', 'Elektroinstallation'],
    ['arbeit', 'Montage'],
    ['sonstiges', 'Sonstiges'],
  ] as const;

  return (
    <section>
      <h2>
        Kalkulation <Herkunftsmarke herkunft={herkunft} />
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
        <Zahlenfeld
          beschriftung="Stundensatz"
          einheit="Euro"
          schritt={0.5}
          wert={wert.stundensatz}
          lesbar={lesbar}
          onAendern={(neu) => aendere('stundensatz', neu)}
          fehltHinweis="Ohne Stundensatz bleibt die Montage im Angebot ohne Betrag."
        />
        <Zahlenfeld
          beschriftung="Stunden je Modul"
          schritt={0.05}
          wert={wert.stundenJeModul}
          lesbar={lesbar}
          onAendern={(neu) => aendere('stundenJeModul', neu ?? 0)}
        />
        <Zahlenfeld
          beschriftung="Grundstunden je Projekt"
          schritt={0.5}
          wert={wert.grundstunden}
          lesbar={lesbar}
          onAendern={(neu) => aendere('grundstunden', neu ?? 0)}
        />
        <Zahlenfeld
          beschriftung="Verlegezuschlag Kabel"
          einheit="Prozent"
          wert={wert.kabelzuschlagProzent}
          lesbar={lesbar}
          onAendern={(neu) => aendere('kabelzuschlagProzent', neu ?? 0)}
        />
      </div>

      <h3 style={{ marginTop: '1.25rem' }}>Aufschläge je Warengruppe</h3>
      <p style={{ fontSize: '0.8rem', color: 'var(--grau)', margin: '0 0 0.5rem' }}>
        Module, Wechselrichter, Speicher und Montagematerial werden unterschiedlich kalkuliert
        (PV-J08). Leer heißt: kein Aufschlag.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.6rem' }}>
        {warengruppen.map(([schluessel, text]) => (
          <Zahlenfeld
            key={schluessel}
            beschriftung={text}
            einheit="%"
            wert={wert.aufschlaege?.[schluessel] ?? null}
            lesbar={lesbar}
            onAendern={(neu) => {
              const aufschlaege = { ...(wert.aufschlaege ?? {}) };
              if (neu === null) delete aufschlaege[schluessel];
              else aufschlaege[schluessel] = neu;
              aendere('aufschlaege', aufschlaege);
            }}
          />
        ))}
      </div>

      {herkunft === 'projekt' && (
        <button
          onClick={() => void nimmZurueck('kalkulation.standard')}
          style={{ marginTop: '1rem' }}
        >
          Wieder global rechnen
        </button>
      )}
    </section>
  );
}

function Wirtschaftlichkeit({
  quelle,
  setze,
  nimmZurueck,
  lesbar,
}: Werteigenschaften): React.JSX.Element {
  const preise = loeseAuf(
    quelle,
    'wirtschaftlichkeit.preise',
    VORGABEN['wirtschaftlichkeit.preise'],
  );
  const anlage = loeseAuf(
    quelle,
    'wirtschaftlichkeit.anlage',
    VORGABEN['wirtschaftlichkeit.anlage'],
  );

  return (
    <section>
      <h2>
        Preise und Annahmen <Herkunftsmarke herkunft={preise.herkunft} />
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
        <Zahlenfeld
          beschriftung="Strompreis"
          einheit="Euro/kWh"
          schritt={0.01}
          wert={preise.wert.strompreisJeKwh}
          lesbar={lesbar}
          onAendern={(neu) =>
            void setze('wirtschaftlichkeit.preise', { ...preise.wert, strompreisJeKwh: neu })
          }
          fehltHinweis="Ohne Strompreis keine Amortisation."
        />
        <Zahlenfeld
          beschriftung="Einspeisevergütung"
          einheit="Euro/kWh"
          schritt={0.01}
          wert={preise.wert.einspeiseverguetungJeKwh}
          lesbar={lesbar}
          onAendern={(neu) =>
            void setze('wirtschaftlichkeit.preise', {
              ...preise.wert,
              einspeiseverguetungJeKwh: neu,
            })
          }
          fehltHinweis="Ohne Vergütung kein Einspeiseerlös."
        />
        <Zahlenfeld
          beschriftung="Preissteigerung Bezug"
          einheit="Prozent/Jahr"
          schritt={0.5}
          wert={preise.wert.preissteigerungProzent}
          lesbar={lesbar}
          onAendern={(neu) =>
            void setze('wirtschaftlichkeit.preise', {
              ...preise.wert,
              preissteigerungProzent: neu ?? 0,
            })
          }
        />
        <Zahlenfeld
          beschriftung="Degradation Module"
          einheit="Prozent/Jahr"
          schritt={0.1}
          wert={preise.wert.degradationProzent}
          lesbar={lesbar}
          onAendern={(neu) =>
            void setze('wirtschaftlichkeit.preise', {
              ...preise.wert,
              degradationProzent: neu ?? 0,
            })
          }
        />
        <Zahlenfeld
          beschriftung="Betriebskosten"
          einheit="Euro/Jahr"
          schritt={10}
          wert={preise.wert.betriebskostenJeJahr}
          lesbar={lesbar}
          onAendern={(neu) =>
            void setze('wirtschaftlichkeit.preise', { ...preise.wert, betriebskostenJeJahr: neu })
          }
        />
        <Zahlenfeld
          beschriftung="Betrachtungszeitraum"
          einheit="Jahre"
          wert={preise.wert.jahre}
          lesbar={lesbar}
          onAendern={(neu) =>
            void setze('wirtschaftlichkeit.preise', { ...preise.wert, jahre: neu ?? 20 })
          }
        />
      </div>
      <p style={{ fontSize: '0.78rem', color: 'var(--grau)' }}>
        Eine vorsichtige Preissteigerung ist Absicht: Die Amortisation soll nicht schöngerechnet
        werden.
      </p>

      <h2 style={{ marginTop: '1.5rem' }}>
        Anlage und Speicher <Herkunftsmarke herkunft={anlage.herkunft} />
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
        <Zahlenfeld
          beschriftung="Anlagengüte"
          einheit="Prozent"
          wert={anlage.wert.anlagengueteProzent}
          lesbar={lesbar}
          onAendern={(neu) =>
            void setze('wirtschaftlichkeit.anlage', {
              ...anlage.wert,
              anlagengueteProzent: neu ?? 84,
            })
          }
        />
        <Zahlenfeld
          beschriftung="Speicherpreis"
          einheit="Euro/kWh"
          schritt={10}
          wert={anlage.wert.speicherpreisJeKwh}
          lesbar={lesbar}
          onAendern={(neu) =>
            void setze('wirtschaftlichkeit.anlage', { ...anlage.wert, speicherpreisJeKwh: neu })
          }
          fehltHinweis="Ohne Speicherpreis kein Größenvorschlag."
        />
        <label style={{ fontSize: '0.7rem', color: 'var(--grau)', display: 'block' }}>
          Größen im Vergleich, mit Komma
          <input
            defaultValue={anlage.wert.speichergroessen.join(', ')}
            disabled={!lesbar}
            onBlur={(ereignis) =>
              void setze('wirtschaftlichkeit.anlage', {
                ...anlage.wert,
                speichergroessen: ereignis.target.value
                  .split(',')
                  .map((teil) => Number(teil.trim()))
                  .filter((zahl) => Number.isFinite(zahl) && zahl > 0),
              })
            }
          />
        </label>
      </div>

      {(preise.herkunft === 'projekt' || anlage.herkunft === 'projekt') && (
        <button
          onClick={() => {
            void nimmZurueck('wirtschaftlichkeit.preise');
            void nimmZurueck('wirtschaftlichkeit.anlage');
          }}
          style={{ marginTop: '1rem' }}
        >
          Wieder global rechnen
        </button>
      )}
    </section>
  );
}

function Planungswerte({
  quelle,
  setze,
  nimmZurueck,
  lesbar,
}: Werteigenschaften): React.JSX.Element {
  const raster = loeseAuf(quelle, 'raster.standard', VORGABEN['raster.standard']);
  const auslegung = loeseAuf(quelle, 'auslegung.temperatur', VORGABEN['auslegung.temperatur']);
  const vorladen = loeseAuf(quelle, 'offline.vorladen', VORGABEN['offline.vorladen']);

  return (
    <section>
      <h2>
        Randabstände und Fugen <Herkunftsmarke herkunft={raster.herkunft} />
      </h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--grau)', margin: '0 0 0.5rem' }}>
        Betriebsstandard, damit nicht jede Fläche neu eingestellt werden muss (PV-F02).
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem' }}>
        {(
          [
            ['randTraufeMm', 'Traufe'],
            ['randFirstMm', 'First'],
            ['randOrtgangMm', 'Ortgang'],
            ['sicherheitsabstandMm', 'um Hindernisse'],
            ['fugeQuerMm', 'Fuge quer'],
            ['fugeLaengsMm', 'Fuge längs'],
          ] as const
        ).map(([feld, text]) => (
          <Zahlenfeld
            key={feld}
            beschriftung={text}
            einheit="mm"
            wert={raster.wert[feld]}
            lesbar={lesbar}
            onAendern={(neu) => void setze('raster.standard', { ...raster.wert, [feld]: neu ?? 0 })}
          />
        ))}
      </div>

      <h2 style={{ marginTop: '1.5rem' }}>
        Auslegungstemperaturen <Herkunftsmarke herkunft={auslegung.herkunft} />
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem' }}>
        <Zahlenfeld
          beschriftung="Kalt (Stringgrenze)"
          einheit="Grad"
          wert={auslegung.wert.kaltGradC}
          lesbar={lesbar}
          onAendern={(neu) =>
            void setze('auslegung.temperatur', { ...auslegung.wert, kaltGradC: neu ?? -10 })
          }
        />
        <Zahlenfeld
          beschriftung="Warm (MPP-Bereich)"
          einheit="Grad"
          wert={auslegung.wert.warmGradC}
          lesbar={lesbar}
          onAendern={(neu) =>
            void setze('auslegung.temperatur', { ...auslegung.wert, warmGradC: neu ?? 70 })
          }
        />
      </div>

      <h2 style={{ marginTop: '1.5rem' }}>
        Offline vorladen <Herkunftsmarke herkunft={vorladen.herkunft} />
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem' }}>
        <Zahlenfeld
          beschriftung="Umkreis"
          einheit="m"
          schritt={50}
          wert={vorladen.wert.umkreisM}
          lesbar={lesbar}
          onAendern={(neu) =>
            void setze('offline.vorladen', { ...vorladen.wert, umkreisM: neu ?? 200 })
          }
        />
        <Zahlenfeld
          beschriftung="Hinweis ab"
          einheit="Tagen"
          wert={vorladen.wert.hinweisAbTagen}
          lesbar={lesbar}
          onAendern={(neu) =>
            void setze('offline.vorladen', { ...vorladen.wert, hinweisAbTagen: neu ?? 7 })
          }
        />
      </div>

      {[raster, auslegung, vorladen].some((w) => w.herkunft === 'projekt') && (
        <button
          onClick={() => {
            void nimmZurueck('raster.standard');
            void nimmZurueck('auslegung.temperatur');
            void nimmZurueck('offline.vorladen');
          }}
          style={{ marginTop: '1rem' }}
        >
          Wieder global rechnen
        </button>
      )}
    </section>
  );
}

/**
 * Errichter — steht auf Pruefbefund und Anlagendokumentation.
 *
 * Das Pruefprotokoll darf nur ein konzessioniertes Unternehmen ausstellen, das
 * damit die Haftung fuer die ordnungsgemaesse Errichtung uebernimmt. Deshalb
 * gehoert die Konzessionsnummer aufs Blatt — sie ist ueber firmen.wko.at
 * nachpruefbar.
 */
function Errichter({
  quelle,
  setze,
  lesbar,
}: {
  quelle: Einstellungsquelle;
  setze: (schluessel: string, wert: unknown) => Promise<void>;
  lesbar: boolean;
}): React.JSX.Element {
  const { wert, herkunft } = loeseAuf(quelle, 'errichter.angaben', {
    firma: null as string | null,
    strasse: null as string | null,
    plz: null as string | null,
    ort: null as string | null,
    telefon: null as string | null,
    konzession: null as string | null,
    ausgefuehrtNach: 'OVE Richtlinie R11',
  });

  const feld = (schluessel: keyof typeof wert, text: string): React.JSX.Element => (
    <label style={{ fontSize: '0.7rem', color: 'var(--grau)', display: 'block' }}>
      {text}
      <input
        defaultValue={(wert[schluessel] as string) ?? ''}
        disabled={!lesbar}
        onBlur={(ereignis) =>
          void setze('errichter.angaben', { ...wert, [schluessel]: ereignis.target.value || null })
        }
      />
    </label>
  );

  return (
    <section>
      <h2>
        Errichter <Herkunftsmarke herkunft={herkunft} />
      </h2>
      <p className="klein">Steht auf dem Prüfbefund und auf der Anlagendokumentation.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.6rem' }}>
        {feld('firma', 'Firma')}
        {feld('konzession', 'Konzession')}
        {feld('strasse', 'Straße')}
        {feld('telefon', 'Telefon')}
        {feld('plz', 'PLZ')}
        {feld('ort', 'Ort')}
        <label style={{ fontSize: '0.7rem', color: 'var(--grau)', display: 'block' }}>
          Ausgeführt nach
          <select
            defaultValue={wert.ausgefuehrtNach}
            disabled={!lesbar}
            onChange={(ereignis) =>
              void setze('errichter.angaben', { ...wert, ausgefuehrtNach: ereignis.target.value })
            }
          >
            <option>OVE Richtlinie R11</option>
            <option>OVE Richtlinie R6-2-1</option>
            <option>OVE Richtlinie R6-2-2</option>
          </select>
        </label>
      </div>
      {!wert.konzession && (
        <div className="hinweis">
          Ohne Konzessionsnummer fehlt sie auf jedem Befund. Nachprüfbar über firmen.wko.at.
        </div>
      )}
    </section>
  );
}

/**
 * Die Prüfpunkte des Befunds (Mathias, 11/2026). Als Text, weil eine Tabelle
 * mit Abschnitten am Tablet mühsamer wäre: Zeile ohne Strich beginnt einen
 * Abschnitt, Zeile mit Strich ist ein Punkt. Übernommen wird beim Verlassen.
 */
function Pruefvorlagen({ quelle, setze, nimmZurueck, lesbar }: Werteigenschaften): React.JSX.Element {
  const { wert, herkunft } = loeseAuf(quelle, 'pruefbefund.vorlage', VORGABEN['pruefbefund.vorlage']);
  const text = vorlageAlsText(wert);
  const anzahl = wert.abschnitte.reduce((summe, a) => summe + a.punkte.length, 0);
  return (
    <section>
      <h2 style={{ marginTop: '1.5rem' }}>
        Prüfpunkte im Befund <Herkunftsmarke herkunft={herkunft} />
      </h2>
      <p className="klein">
        Besichtigen und Erproben, abgehakt mit „i. O.“, „Mangel“ oder „entfällt“. Eine Zeile ohne
        Strich beginnt einen Abschnitt, eine Zeile mit „-“ ist ein Punkt. Ein Befund übernimmt die
        Liste beim ersten Haken — spätere Änderungen hier ändern ihn nicht mehr.
      </p>
      <p className="klein gedaempft">
        Vorschlag nach dem Aufbau von OVE/ÖNORM E 8101 und IEC 62446-1. Gegen das eigene
        Prüfprotokoll abgleichen. Derzeit {anzahl} Punkte.
      </p>
      <textarea
        key={text}
        rows={18}
        defaultValue={text}
        disabled={!lesbar}
        style={{ width: '100%', fontFamily: 'inherit' }}
        onBlur={(ereignis) => {
          const neu = vorlageAusText(ereignis.target.value);
          if (vorlageAlsText(neu) !== text) void setze('pruefbefund.vorlage', neu);
        }}
      />
      {herkunft === 'projekt' && (
        <button className="schlicht" onClick={() => void nimmZurueck('pruefbefund.vorlage')}>
          Für dieses Projekt zurücknehmen
        </button>
      )}
    </section>
  );
}

/** Nach wie vielen Tagen an einen offenen Schritt erinnert wird (Mathias, 11/2026). */
function Erinnerungsfristen({ quelle, setze, lesbar }: Werteigenschaften): React.JSX.Element {
  const { wert, herkunft } = loeseAuf(quelle, 'abwicklung.erinnerung', VORGABEN['abwicklung.erinnerung']);
  const feld = (schluessel: keyof typeof wert, text: string): React.JSX.Element => (
    <Zahlenfeld
      key={schluessel}
      beschriftung={text}
      einheit="Tagen"
      wert={wert[schluessel]}
      lesbar={lesbar}
      onAendern={(neu) => void setze('abwicklung.erinnerung', { ...wert, [schluessel]: neu ?? 0 })}
    />
  );
  return (
    <section>
      <h2 style={{ marginTop: '1.5rem' }}>
        Erinnerungen <Herkunftsmarke herkunft={herkunft} />
      </h2>
      <p className="klein">
        Wann ein offener Schritt auf der Startseite als fällig erscheint, wenn im Projekt kein Datum gesetzt ist —
        gezählt ab dem Auftrag, bei der Fertigmeldung ab der Abnahme. Vorschlagswerte, keine Fristen des
        Netzbetreibers.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.6rem' }}>
        {feld('netzzugangsvertragTage', 'Netzzugangsvertrag nach')}
        {feld('stromliefervertragTage', 'Stromliefervertrag nach')}
        {feld('foerderansuchenTage', 'Förderansuchen nach')}
        {feld('fertigmeldungTage', 'Fertigmeldung nach')}
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Stammdaten
// -----------------------------------------------------------------------------
const spalte: React.CSSProperties = {
  borderBottom: '1px solid var(--linie)',
  padding: '0.3rem 0.4rem 0.3rem 0',
  verticalAlign: 'top',
};

const kopfzelle: React.CSSProperties = {
  textAlign: 'left',
  fontSize: '0.6rem',
  textTransform: 'uppercase',
  letterSpacing: '0.14em',
  color: 'var(--grau)',
  fontWeight: 300,
  borderBottom: '1px solid var(--schwarz)',
  padding: '0.3rem 0.4rem 0.3rem 0',
};

/** Kleines Zahlenfeld in einer Tabellenzeile. */
function Zelle({
  wert,
  darf,
  schritt = 1,
  breite = '5rem',
  onAendern,
}: {
  wert: number | string | null | undefined;
  darf: boolean;
  schritt?: number;
  breite?: string;
  onAendern: (wert: string) => void;
}): React.JSX.Element {
  return (
    <input
      // Neu aufbauen, wenn der Wert sich ändert — sonst bleibt ein abgewiesener
      // Eintrag stehen und sieht gespeichert aus.
      key={String(wert ?? '')}
      type={typeof wert === 'string' || wert === null ? 'text' : 'number'}
      step={schritt}
      defaultValue={wert ?? ''}
      disabled={!darf}
      onBlur={(ereignis) => {
        const feld = ereignis.target;
        if (feld.value === String(wert ?? '')) return;
        onAendern(feld.value);
        // Nimmt die Ablage den Wert an, baut sich das Feld mit ihm neu auf.
        // Weist sie ihn ab (leeres Pflichtfeld, keine Zahl), steht danach
        // wieder der gültige Wert da statt des abgewiesenen.
        setTimeout(() => {
          if (feld.isConnected) feld.value = String(wert ?? '');
        }, 400);
      }}
      style={{ width: breite, padding: '0.2rem 0.3rem' }}
    />
  );
}

/**
 * Modulpool (PV-F: Stammdaten).
 *
 * Der erste konkrete Zulieferbedarf von Wania. Bis die Datenblaetter da sind,
 * stehen hier die Platzhalter aus der Spec — gekennzeichnet, damit niemand mit
 * ihnen ein Angebot schreibt und es fuer bare Muenze nimmt.
 */
function Modulpool({ darf }: { darf: boolean }): React.JSX.Element {
  const module = useLiveQuery(
    () => db.module.filter((m) => !m.geloescht_am).toArray(),
    [],
    [] as Modul[],
  );

  async function aendere(modul: Modul, feld: string, roh: string): Promise<void> {
    const zahlenfelder = [
      'breite_mm',
      'hoehe_mm',
      'tiefe_mm',
      'nennleistung_wp',
      'uoc_v',
      'umpp_v',
      'impp_a',
      'isc_a',
      'tk_uoc_prozent_k',
      'tk_isc_prozent_k',
      'max_systemspannung_v',
      'gewicht_kg',
      'einkaufspreis',
      'aufschlag_prozent',
    ];
    // Ohne diese Werte rechnet nichts — leer heißt hier: alten Wert behalten.
    const pflicht = ['breite_mm', 'hoehe_mm', 'nennleistung_wp', 'uoc_v', 'umpp_v', 'tk_uoc_prozent_k'];
    const text = roh.trim().replace(',', '.');
    if (pflicht.includes(feld) && text === '') return;
    let wert = zahlenfelder.includes(feld) ? (text === '' ? null : Number(text)) : roh.trim() || null;
    if (typeof wert === 'number' && !Number.isFinite(wert)) return;
    // Der Koeffizient ist immer negativ; ohne Minus getippt wird er es.
    if (feld === 'tk_uoc_prozent_k' && typeof wert === 'number') wert = -Math.abs(wert);
    if (wert === (modul as unknown as Record<string, unknown>)[feld]) return;
    await speichere('module', modul, {
      [feld]: wert,
      // Sobald jemand einen echten Wert eintraegt, ist es kein Platzhalter mehr.
      ...(feld === 'einkaufspreis' && wert !== null ? { preis_stand: heute() } : {}),
    });
  }

  async function legeAn(): Promise<void> {
    const modul: Modul = {
      id: neueId(),
      hersteller: '',
      typ: 'Neues Modul',
      breite_mm: 1134,
      hoehe_mm: 1762,
      nennleistung_wp: 440,
      uoc_v: 38.5,
      umpp_v: 32.5,
      tk_uoc_prozent_k: -0.25,
      gewicht_kg: null,
      einkaufspreis: null,
      aufschlag_prozent: null,
      // Inaktiv und als Platzhalter: Die Werte oben sind die aus der Spec,
      // nicht die des neuen Moduls. Ein geratener Temperaturkoeffizient ist der
      // teuerste Fehler, den dieses Programm machen kann — deshalb kommt das
      // Modul erst ins Projekt, wenn jemand die Datenblattwerte bestätigt hat.
      aktiv: false,
      ist_platzhalter: true,
      seq: null,
    };
    await speichere('module', modul, {
      hersteller: modul.hersteller,
      typ: modul.typ,
      breite_mm: modul.breite_mm,
      hoehe_mm: modul.hoehe_mm,
      nennleistung_wp: modul.nennleistung_wp,
      uoc_v: modul.uoc_v,
      umpp_v: modul.umpp_v,
      tk_uoc_prozent_k: modul.tk_uoc_prozent_k,
      aktiv: false,
      ist_platzhalter: true,
    });
  }

  const platzhalter = module.filter((m) => m.ist_platzhalter && m.aktiv).length;
  // Weitere Datenblattwerte stehen in einer aufklappbaren Zeile — in einer
  // Reihe wären es siebzehn Spalten, am Tablet nicht mehr zu bedienen.
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const schalte = (id: string): void =>
    setOffen((alt) => {
      const neu = new Set(alt);
      if (neu.has(id)) neu.delete(id);
      else neu.add(id);
      return neu;
    });

  return (
    <section>
      <h2>Modulpool</h2>
      {platzhalter > 0 && (
        <div className="hinweis">
          {platzhalter} Modul(e) sind noch Platzhalter aus der Spezifikation. Für ein belastbares
          Angebot braucht es Abmessungen, U_oc und Temperaturkoeffizient aus dem Datenblatt.
        </div>
      )}

      <div className="knopfreihe">
        <button className="wichtig" onClick={() => void legeAn()} disabled={!darf}>
          Modul anlegen
        </button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: '0.78rem', minWidth: '52rem' }}>
          <thead>
            <tr>
              <th style={kopfzelle}>Hersteller</th>
              <th style={kopfzelle}>Typ</th>
              <th style={kopfzelle}>Breite</th>
              <th style={kopfzelle}>Höhe</th>
              <th style={kopfzelle}>Wp</th>
              <th style={kopfzelle}>U_oc</th>
              <th style={kopfzelle}>U_mpp</th>
              <th style={kopfzelle}>TK %/K</th>
              <th style={kopfzelle}>Einkauf</th>
              <th style={kopfzelle}>Aktiv</th>
              <th style={kopfzelle} />
            </tr>
          </thead>
          <tbody>
            {module.map((modul) => (
              <Fragment key={modul.id}>
              <tr style={{ opacity: modul.aktiv ? 1 : 0.5 }}>
                <td style={spalte}>
                  <Zelle
                    wert={modul.hersteller}
                    darf={darf}
                    breite="7rem"
                    onAendern={(w) => void aendere(modul, 'hersteller', w)}
                  />
                </td>
                <td style={spalte}>
                  <Zelle
                    wert={modul.typ}
                    darf={darf}
                    breite="10rem"
                    onAendern={(w) => void aendere(modul, 'typ', w)}
                  />
                  {modul.ist_platzhalter && (
                    <span style={{ display: 'block', color: 'var(--waerme)', fontSize: '0.65rem' }}>
                      Platzhalter
                      {darf && (
                        <button
                          className="schlicht"
                          style={{ fontSize: '0.7rem', marginLeft: '0.3rem' }}
                          onClick={() => {
                            if (
                              confirm(
                                `Stimmen Maße, Leistung, U_oc, U_mpp und Temperaturkoeffizient von „${modul.typ}“ mit dem Datenblatt überein?`,
                              )
                            )
                              void speichere('module', modul, { ist_platzhalter: false });
                          }}
                        >
                          Werte geprüft
                        </button>
                      )}
                    </span>
                  )}
                </td>
                <td style={spalte}>
                  <Zelle
                    wert={modul.breite_mm}
                    darf={darf}
                    onAendern={(w) => void aendere(modul, 'breite_mm', w)}
                  />
                </td>
                <td style={spalte}>
                  <Zelle
                    wert={modul.hoehe_mm}
                    darf={darf}
                    onAendern={(w) => void aendere(modul, 'hoehe_mm', w)}
                  />
                </td>
                <td style={spalte}>
                  <Zelle
                    wert={modul.nennleistung_wp}
                    darf={darf}
                    onAendern={(w) => void aendere(modul, 'nennleistung_wp', w)}
                  />
                </td>
                <td style={spalte}>
                  <Zelle
                    wert={modul.uoc_v}
                    darf={darf}
                    schritt={0.1}
                    onAendern={(w) => void aendere(modul, 'uoc_v', w)}
                  />
                </td>
                <td style={spalte}>
                  <Zelle
                    wert={modul.umpp_v}
                    darf={darf}
                    schritt={0.1}
                    onAendern={(w) => void aendere(modul, 'umpp_v', w)}
                  />
                </td>
                <td style={spalte}>
                  <Zelle
                    wert={modul.tk_uoc_prozent_k}
                    darf={darf}
                    schritt={0.01}
                    onAendern={(w) => void aendere(modul, 'tk_uoc_prozent_k', w)}
                  />
                </td>
                <td style={spalte}>
                  <Zelle
                    wert={modul.einkaufspreis}
                    darf={darf}
                    schritt={0.5}
                    onAendern={(w) => void aendere(modul, 'einkaufspreis', w)}
                  />
                </td>
                <td style={spalte}>
                  <input
                    type="checkbox"
                    checked={modul.aktiv}
                    disabled={!darf}
                    onChange={() => void speichere('module', modul, { aktiv: !modul.aktiv })}
                    style={{ width: 'auto', minHeight: 0 }}
                  />
                </td>
                <td style={spalte}>
                  <button
                    className="schlicht"
                    aria-expanded={offen.has(modul.id)}
                    onClick={() => schalte(modul.id)}
                  >
                    {offen.has(modul.id) ? 'Weniger' : 'Mehr'}
                  </button>
                </td>
              </tr>
              {offen.has(modul.id) && (
                <tr style={{ opacity: modul.aktiv ? 1 : 0.5 }}>
                  <td colSpan={11} style={{ ...spalte, paddingBottom: '0.75rem' }}>
                    <div className="moduldetails">
                      <label>
                        Modulart
                        <input
                          list="modularten"
                          defaultValue={modul.modulart ?? ''}
                          disabled={!darf}
                          onBlur={(e) => void aendere(modul, 'modulart', e.target.value)}
                        />
                      </label>
                      <label title="Kurzschlussstrom I_sc">
                        I_sc A
                        <Zelle wert={modul.isc_a ?? null} darf={darf} schritt={0.01} onAendern={(w) => void aendere(modul, 'isc_a', w)} />
                      </label>
                      <label title="Strom im MPP">
                        I_mpp A
                        <Zelle wert={modul.impp_a ?? null} darf={darf} schritt={0.01} onAendern={(w) => void aendere(modul, 'impp_a', w)} />
                      </label>
                      <label title="Temperaturkoeffizient des Stroms (Datenblatt: α I_sc), positiv">
                        TK I %/K
                        <Zelle wert={modul.tk_isc_prozent_k ?? null} darf={darf} schritt={0.001} onAendern={(w) => void aendere(modul, 'tk_isc_prozent_k', w)} />
                      </label>
                      <label title="Höchste Systemspannung laut Datenblatt — geht in die Stringprüfung ein">
                        U_sys max V
                        <Zelle wert={modul.max_systemspannung_v ?? null} darf={darf} schritt={100} onAendern={(w) => void aendere(modul, 'max_systemspannung_v', w)} />
                      </label>
                      <label>
                        Gewicht kg
                        <Zelle wert={modul.gewicht_kg ?? null} darf={darf} schritt={0.1} onAendern={(w) => void aendere(modul, 'gewicht_kg', w)} />
                      </label>
                      <label>
                        Tiefe mm
                        <Zelle wert={modul.tiefe_mm ?? null} darf={darf} onAendern={(w) => void aendere(modul, 'tiefe_mm', w)} />
                      </label>
                      <label>
                        Datenblatt
                        <Datenblattwahl
                          wert={modul.datenblatt_id}
                          darf={darf}
                          onAendern={(id) => void speichere('module', modul, { datenblatt_id: id })}
                        />
                      </label>
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <datalist id="modularten">
        <option value="Glas-Folie" />
        <option value="Glas-Glas" />
        <option value="Bifazial Glas-Glas" />
        <option value="Full Black" />
      </datalist>
      <p style={{ fontSize: '0.75rem', color: 'var(--grau)' }}>
        Ausgelaufene Module bleiben für die Dokumentation bestehender Anlagen erhalten — Haken
        entfernen statt löschen.
      </p>
    </section>
  );
}

/** Wechselrichter, Speicher, Wallbox, Heizstab, Ueberspannungsschutz. */
function Komponentenpool({ darf }: { darf: boolean }): React.JSX.Element {
  const komponenten = useLiveQuery(
    () => db.komponenten.filter((k) => !k.geloescht_am).toArray(),
    [],
    [] as Komponente[],
  );

  async function aendereKenndaten(k: Komponente, feld: string, roh: string): Promise<void> {
    const wert = roh === '' ? undefined : Number(roh);
    const kenndaten = { ...k.kenndaten };
    if (wert === undefined) delete (kenndaten as Record<string, unknown>)[feld];
    else (kenndaten as Record<string, unknown>)[feld] = wert;
    await speichere('komponenten', k, { kenndaten });
  }

  /**
   * Strom je Tracker als Liste: "25; 16,5". Ein einzelner Wert gilt für alle
   * Tracker — bei den meisten Geräten ab drei Trackern sind sie gleich.
   */
  async function aendereStroeme(k: Komponente, roh: string): Promise<void> {
    const werte = roh
      .split(/[;/]|\s+/)
      .map((t) => t.trim().replace(',', '.'))
      .filter(Boolean)
      .map(Number)
      .filter((n) => Number.isFinite(n) && n > 0);
    const anzahl = Number(k.kenndaten.anzahlMppt ?? werte.length) || werte.length;
    const liste = werte.length === 1 ? Array.from({ length: anzahl }, () => werte[0]!) : werte;
    const kenndaten = { ...k.kenndaten } as Record<string, unknown>;
    if (liste.length === 0) delete kenndaten['maxStromJeMpptA'];
    else kenndaten['maxStromJeMpptA'] = liste;
    await speichere('komponenten', k, { kenndaten });
  }

  async function legeAn(art: Komponentenart): Promise<void> {
    const k: Komponente = {
      id: neueId(),
      art,
      hersteller: '',
      typ: 'Neu',
      // Leer, nicht vorbelegt: Ein Wechselrichter mit erfundenen 1000 V ließe
      // Strings durch, die das echte Gerät nicht verträgt. Fehlen die Werte,
      // sagt die Stringprüfung das.
      kenndaten: {},
      einkaufspreis: null,
      warengruppe:
        art === 'speicher'
          ? 'speicher'
          : art === 'wechselrichter'
            ? 'wechselrichter'
            : art === 'wallbox' || art === 'heizstab'
              ? 'sonstiges'
              : 'elektromaterial',
      aktiv: true,
      ist_platzhalter: art === 'wechselrichter' || art === 'speicher',
      seq: null,
    };
    await speichere('komponenten', k, {
      art: k.art,
      hersteller: k.hersteller,
      typ: k.typ,
      kenndaten: k.kenndaten,
      warengruppe: k.warengruppe,
      aktiv: true,
      ist_platzhalter: k.ist_platzhalter,
    });
  }

  const arten: Array<[Komponentenart, string]> = [
    ['wechselrichter', 'Wechselrichter'],
    ['speicher', 'Speicher'],
    ['wallbox', 'Wallbox'],
    ['heizstab', 'Heizstab'],
    ['ueberspannungsschutz', 'Überspannungsschutz'],
    ['smartmeter', 'Smart-Meter'],
    ['notstrom', 'Netz-Not-Umschaltung'],
    ['wlv_zk', 'WLV im Zählerkasten'],
    ['wlv_extern', 'WLV extern'],
    ['internet', 'Internetanschluss'],
    ['sicherungstrennschalter', 'Sicherungstrennschalter'],
    ['fi_ls', 'FI/LS am Wechselrichter'],
    ['zaehlersteckleiste', 'Zählersteckleiste'],
  ];

  return (
    <section>
      <h2>Komponenten</h2>
      <div className="knopfreihe">
        {arten.map(([art, text]) => (
          <button key={art} onClick={() => void legeAn(art)} disabled={!darf}>
            + {text}
          </button>
        ))}
      </div>

      {arten.map(([art, text]) => {
        const gruppe = komponenten.filter((k) => k.art === art);
        if (gruppe.length === 0) return null;
        return (
          <div key={art} style={{ marginBottom: '1.25rem' }}>
            <h3>{text}</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: '0.78rem', minWidth: '44rem' }}>
                <thead>
                  <tr>
                    <th style={kopfzelle}>Hersteller</th>
                    <th style={kopfzelle}>Typ</th>
                    {art === 'wechselrichter' && (
                      <>
                        <th style={kopfzelle}>MPPT</th>
                        <th style={kopfzelle}>U_max V</th>
                        <th style={kopfzelle}>U_min V</th>
                        <th style={kopfzelle}>MPP ab V</th>
                        <th style={kopfzelle} title="Je Tracker, mit ; getrennt — ein Wert gilt für alle">
                          I_max je MPPT A
                        </th>
                        <th style={kopfzelle}>PV max kW</th>
                        <th style={kopfzelle}>AC kW</th>
                      </>
                    )}
                    {art === 'speicher' && (
                      <>
                        <th style={kopfzelle}>kWh</th>
                        <th style={kopfzelle}>Laden kW</th>
                        <th style={kopfzelle}>Entladen kW</th>
                      </>
                    )}
                    <th style={kopfzelle}>Datenblatt</th>
                    <th style={kopfzelle}>Einkauf</th>
                    <th style={kopfzelle}>Aktiv</th>
                  </tr>
                </thead>
                <tbody>
                  {gruppe.map((k) => (
                    <tr key={k.id} style={{ opacity: k.aktiv ? 1 : 0.5 }}>
                      <td style={spalte}>
                        <Zelle
                          wert={k.hersteller}
                          darf={darf}
                          breite="7rem"
                          onAendern={(w) => void speichere('komponenten', k, { hersteller: w })}
                        />
                      </td>
                      <td style={spalte}>
                        <Zelle
                          wert={k.typ}
                          darf={darf}
                          breite="10rem"
                          onAendern={(w) => void speichere('komponenten', k, { typ: w })}
                        />
                        {k.ist_platzhalter && (
                          <span
                            style={{
                              display: 'block',
                              color: 'var(--waerme)',
                              fontSize: '0.65rem',
                            }}
                          >
                            Platzhalter
                            {darf && (
                              <button
                                className="schlicht"
                                style={{ fontSize: '0.7rem', marginLeft: '0.3rem' }}
                                onClick={() => {
                                  if (confirm(`Stimmen die Kenndaten von „${k.typ}“ mit dem Datenblatt überein?`))
                                    void speichere('komponenten', k, { ist_platzhalter: false });
                                }}
                              >
                                Werte geprüft
                              </button>
                            )}
                          </span>
                        )}
                      </td>
                      {art === 'wechselrichter' &&
                        (
                          [
                            'anzahlMppt',
                            'maxEingangsspannungV',
                            'minEingangsspannungV',
                            'mppUntergrenzeV',
                            'maxStromJeMpptA',
                            'maxPvLeistungKw',
                            'acLeistungKw',
                          ] as const
                        ).map((feld) =>
                          feld === 'maxStromJeMpptA' ? (
                            <td key={feld} style={spalte}>
                              <Zelle
                                wert={(k.kenndaten.maxStromJeMpptA ?? [])
                                  .map((a) => a.toLocaleString('de-AT'))
                                  .join('; ')}
                                darf={darf}
                                breite="7rem"
                                onAendern={(w) => void aendereStroeme(k, w)}
                              />
                            </td>
                          ) : (
                            <td key={feld} style={spalte}>
                              <Zelle
                                wert={(k.kenndaten as Record<string, number>)[feld] ?? null}
                                darf={darf}
                                schritt={0.5}
                                onAendern={(w) => void aendereKenndaten(k, feld, w)}
                              />
                            </td>
                          ),
                        )}
                      {art === 'speicher' &&
                        (['kapazitaetKwh', 'ladeleistungKw', 'entladeleistungKw'] as const).map(
                          (feld) => (
                            <td key={feld} style={spalte}>
                              <Zelle
                                wert={(k.kenndaten as Record<string, number>)[feld] ?? null}
                                darf={darf}
                                schritt={0.5}
                                onAendern={(w) => void aendereKenndaten(k, feld, w)}
                              />
                            </td>
                          ),
                        )}
                      <td style={spalte}>
                        <Datenblattwahl
                          wert={k.datenblatt_id}
                          darf={darf}
                          onAendern={(id) => void speichere('komponenten', k, { datenblatt_id: id })}
                        />
                      </td>
                      <td style={spalte}>
                        <Zelle
                          wert={k.einkaufspreis}
                          darf={darf}
                          schritt={10}
                          onAendern={(w) =>
                            void speichere('komponenten', k, {
                              einkaufspreis: w === '' ? null : Number(w),
                              ...(w !== '' ? { preis_stand: heute() } : {}),
                            })
                          }
                        />
                      </td>
                      <td style={spalte}>
                        <input
                          type="checkbox"
                          checked={k.aktiv}
                          disabled={!darf}
                          onChange={() => void speichere('komponenten', k, { aktiv: !k.aktiv })}
                          style={{ width: 'auto', minHeight: 0 }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </section>
  );
}

const heute = (): string => new Date().toISOString().slice(0, 10);

/**
 * Artikelstamm und Preise (PV-J07).
 *
 * Preise bewegen sich stark — das war im Gespraech mehrfach Thema. Deshalb
 * steht neben jedem Preis, von wann er ist, und deshalb laesst sich hier ein
 * Preis auch nur fuer ein Projekt ueberschreiben: Sonderkondition, Restposten,
 * Kunde stellt bei.
 */
function Artikelstamm({
  darf,
  projektId,
  quelle,
  setze,
}: {
  darf: boolean;
  projektId: string | null;
  quelle: Einstellungsquelle;
  setze: (schluessel: string, wert: unknown) => Promise<void>;
}): React.JSX.Element {
  const artikel = useLiveQuery(
    () => db.artikel.filter((a) => !a.geloescht_am).toArray(),
    [],
    [] as Artikel[],
  );
  const ueberschreibungen = loeseAuf(
    quelle,
    'preise.ueberschreibungen',
    VORGABEN['preise.ueberschreibungen'],
  ).wert as Record<string, number>;

  async function legeAn(): Promise<void> {
    const neu: Artikel = {
      id: neueId(),
      nummer: null,
      bezeichnung: 'Neuer Artikel',
      einheit: 'Stk',
      einkaufspreis: null,
      warengruppe: 'montagematerial',
      preis_stand: null,
      aktiv: true,
      ist_platzhalter: false,
      seq: null,
    };
    await speichere('artikel', neu, {
      nummer: neu.nummer,
      bezeichnung: neu.bezeichnung,
      einheit: neu.einheit,
      warengruppe: neu.warengruppe,
      aktiv: true,
      ist_platzhalter: false,
    });
  }

  const ohnePreis = artikel.filter((a) => a.aktiv && a.einkaufspreis === null).length;

  return (
    <section>
      <h2>Artikel und Preise</h2>
      {ohnePreis > 0 && (
        <div className="hinweis">
          {ohnePreis} aktive Position(en) ohne Preis. In der Materialliste bleibt die betroffene
          Warengruppe ohne Summe — das ist Absicht und kein Fehler.
        </div>
      )}

      <div className="knopfreihe">
        <button className="wichtig" onClick={() => void legeAn()} disabled={!darf}>
          Artikel anlegen
        </button>
      </div>

      {projektId && (
        <div className="hinweis">
          Die Spalte „nur dieses Projekt" überschreibt den Preis für dieses eine Projekt. Leer
          heißt: es gilt der allgemeine Preis.
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: '0.78rem', minWidth: '46rem' }}>
          <thead>
            <tr>
              <th style={kopfzelle}>Nummer</th>
              <th style={kopfzelle}>Bezeichnung</th>
              <th style={kopfzelle}>Einheit</th>
              <th style={kopfzelle}>Warengruppe</th>
              <th style={kopfzelle}>Einkauf</th>
              <th style={kopfzelle}>Stand</th>
              {projektId && <th style={kopfzelle}>nur dieses Projekt</th>}
              <th style={kopfzelle}>Aktiv</th>
            </tr>
          </thead>
          <tbody>
            {artikel.map((a) => (
              <tr key={a.id} style={{ opacity: a.aktiv ? 1 : 0.5 }}>
                <td style={spalte}>
                  <Zelle
                    wert={a.nummer}
                    darf={darf}
                    breite="5rem"
                    onAendern={(w) => void speichere('artikel', a, { nummer: w || null })}
                  />
                </td>
                <td style={spalte}>
                  <Zelle
                    wert={a.bezeichnung}
                    darf={darf}
                    breite="13rem"
                    onAendern={(w) => void speichere('artikel', a, { bezeichnung: w })}
                  />
                </td>
                <td style={spalte}>
                  <Zelle
                    wert={a.einheit}
                    darf={darf}
                    breite="3.5rem"
                    onAendern={(w) => void speichere('artikel', a, { einheit: w })}
                  />
                </td>
                <td style={spalte}>
                  <select
                    value={a.warengruppe}
                    disabled={!darf}
                    onChange={(ereignis) =>
                      void speichere('artikel', a, { warengruppe: ereignis.target.value })
                    }
                    style={{ width: '9rem', padding: '0.2rem' }}
                  >
                    {[
                      'module',
                      'wechselrichter',
                      'speicher',
                      'montagematerial',
                      'elektromaterial',
                      'arbeit',
                      'sonstiges',
                    ].map((gruppe) => (
                      <option key={gruppe} value={gruppe}>
                        {gruppe}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={spalte}>
                  <Zelle
                    wert={a.einkaufspreis}
                    darf={darf}
                    schritt={0.05}
                    onAendern={(w) =>
                      void speichere('artikel', a, {
                        einkaufspreis: w === '' ? null : Number(w),
                        ...(w !== '' ? { preis_stand: heute() } : {}),
                      })
                    }
                  />
                </td>
                <td style={{ ...spalte, fontSize: '0.7rem', color: 'var(--grau)' }}>
                  {tagAnzeige(a.preis_stand) || '—'}
                </td>
                {projektId && (
                  <td style={spalte}>
                    <Zelle
                      wert={ueberschreibungen[a.id] ?? null}
                      darf
                      schritt={0.05}
                      onAendern={(w) => {
                        const neu = { ...ueberschreibungen };
                        if (w === '') delete neu[a.id];
                        else neu[a.id] = Number(w);
                        void setze('preise.ueberschreibungen', neu);
                      }}
                    />
                  </td>
                )}
                <td style={spalte}>
                  <input
                    type="checkbox"
                    checked={a.aktiv}
                    disabled={!darf}
                    onChange={() => void speichere('artikel', a, { aktiv: !a.aktiv })}
                    style={{ width: 'auto', minHeight: 0 }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Materialsätze je Dachtyp und Ausrichtung (PV-J02).
 *
 * „Wania pflegt das selbst, ohne Entwicklereingriff" — genau dafuer ist diese
 * Ansicht da. Was hier steht, entscheidet, welche Anbauteile je Modul in die
 * Materialliste wandern.
 */
function Materialsaetze({ darf }: { darf: boolean }): React.JSX.Element {
  const saetze = useLiveQuery(
    () => db.materialsaetze.filter((s) => !s.geloescht_am).toArray(),
    [],
    [] as Materialsatz[],
  );
  const artikel = useLiveQuery(
    () => db.artikel.filter((a) => a.aktiv && !a.geloescht_am).toArray(),
    [],
    [] as Artikel[],
  );
  const [offen, setOffen] = useState<string | null>(null);

  async function aenderePosition(
    satz: Materialsatz,
    feld: 'je_modul' | 'je_flaeche',
    index: number,
    aenderung: Partial<{ artikelId: string; menge: number }> | null,
  ): Promise<void> {
    const liste = [...satz[feld]];
    if (aenderung === null) liste.splice(index, 1);
    else if (index >= liste.length) liste.push({ artikelId: '', menge: 1, ...aenderung });
    else liste[index] = { ...liste[index]!, ...aenderung };
    await speichere('materialsaetze', satz, { [feld]: liste });
  }

  async function aendereMontage(satz: Materialsatz, aenderung: Partial<Montagesystem>): Promise<void> {
    const neu: Montagesystem = { ...(satz.montage ?? {}), ...aenderung };
    // Leere Rollen ganz entfernen — "kein Artikel" heisst: kommt nicht vor.
    for (const [rolle, wert] of Object.entries(neu)) {
      if (wert === '' || wert === undefined) delete (neu as Record<string, unknown>)[rolle];
    }
    await speichere('materialsaetze', satz, { montage: neu });
  }

  const artikelwahl = (
    wert: string | undefined,
    onWahl: (id: string) => void,
  ): React.JSX.Element => (
    <select
      value={wert ?? ''}
      disabled={!darf}
      onChange={(ereignis) => onWahl(ereignis.target.value)}
      style={{ width: '18rem' }}
    >
      <option value="">— kommt nicht vor —</option>
      {artikel.map((a) => (
        <option key={a.id} value={a.id}>
          {a.bezeichnung}
        </option>
      ))}
    </select>
  );

  return (
    <section>
      <h2>Materialsätze</h2>
      <p className="klein">
        Je Kombination aus Dachtyp und Modulausrichtung. Klemmen, Schienen und Dachhaken
        rechnet das Montagesystem aus der Lage der Module: je durchgehendem Strang zwei
        Schienen, vier Endklemmen und zwei Mittelklemmen je Fuge. Im Hochformat laufen die
        Schienen entlang der Reihe, im Querformat vom First zur Traufe. Was darüber hinaus je
        Modul oder einmal je Fläche anfällt, steht darunter.
      </p>

      {saetze.map((satz) => (
        <div key={satz.id} style={{ borderTop: '1px solid var(--linie)', padding: '0.6rem 0' }}>
          <button
            onClick={() => setOffen(offen === satz.id ? null : satz.id)}
            style={{ width: '100%', textAlign: 'left' }}
          >
            {satz.bezeichnung ?? `${satz.dachtyp} / ${satz.ausrichtung}`}
            <span className="gedaempft">
              {' '}
              · {satz.montage ? 'Montagesystem, ' : ''}
              {satz.je_modul.length} je Modul, {satz.je_flaeche.length} je Fläche
            </span>
          </button>

          {offen === satz.id && (
            <div style={{ marginTop: '0.6rem' }}>
              <p className="marke">Montagesystem</p>
              <div style={{ display: 'grid', gap: '0.35rem' }}>
                <label className="klein">
                  Mittelklemme
                  {artikelwahl(satz.montage?.mittelklemme, (id) =>
                    void aendereMontage(satz, { mittelklemme: id }),
                  )}
                </label>
                <label className="klein">
                  Endklemme
                  {artikelwahl(satz.montage?.endklemme, (id) =>
                    void aendereMontage(satz, { endklemme: id }),
                  )}
                </label>
                <div className="knopfreihe">
                  <label className="klein">
                    Schiene
                    {artikelwahl(satz.montage?.schiene?.artikelId, (id) =>
                      void aendereMontage(satz, {
                        schiene: id ? { artikelId: id, laengeM: satz.montage?.schiene?.laengeM ?? 2.1 } : undefined,
                      }),
                    )}
                  </label>
                  {satz.montage?.schiene && (
                    <label className="klein">
                      Stangenlänge m
                      <Zelle
                        wert={satz.montage.schiene.laengeM}
                        darf={darf}
                        schritt={0.1}
                        onAendern={(w) =>
                          void aendereMontage(satz, {
                            schiene: { artikelId: satz.montage!.schiene!.artikelId, laengeM: Number(w) },
                          })
                        }
                      />
                    </label>
                  )}
                </div>
                <label className="klein">
                  Schienenverbinder
                  {artikelwahl(satz.montage?.schienenverbinder, (id) =>
                    void aendereMontage(satz, { schienenverbinder: id }),
                  )}
                </label>
                <div className="knopfreihe">
                  <label className="klein">
                    Dachhaken
                    {artikelwahl(satz.montage?.dachhaken?.artikelId, (id) =>
                      void aendereMontage(satz, {
                        dachhaken: id ? { artikelId: id, abstandM: satz.montage?.dachhaken?.abstandM ?? 1.2 } : undefined,
                      }),
                    )}
                  </label>
                  {satz.montage?.dachhaken && (
                    <label className="klein">
                      höchstens alle m
                      <Zelle
                        wert={satz.montage.dachhaken.abstandM}
                        darf={darf}
                        schritt={0.1}
                        onAendern={(w) =>
                          void aendereMontage(satz, {
                            dachhaken: { artikelId: satz.montage!.dachhaken!.artikelId, abstandM: Number(w) },
                          })
                        }
                      />
                    </label>
                  )}
                </div>
                <p className="klein gedaempft" style={{ margin: 0 }}>
                  Der Hakenabstand hängt am Sparrenabstand und an Schnee- und Windlast. Das
                  Programm rechnet keine Statik — hier steht der Wert, mit dem Wania montiert.
                </p>
              </div>
            </div>
          )}

          {offen === satz.id &&
            (['je_modul', 'je_flaeche'] as const).map((feld) => (
              <div key={feld} style={{ marginTop: '0.6rem' }}>
                <p className="marke">{feld === 'je_modul' ? 'Je Modul' : 'Einmal je Fläche'}</p>
                {satz[feld].map((position, index) => (
                  <div key={index} className="knopfreihe" style={{ marginBottom: '0.3rem' }}>
                    <select
                      value={position.artikelId}
                      disabled={!darf}
                      onChange={(ereignis) =>
                        void aenderePosition(satz, feld, index, {
                          artikelId: ereignis.target.value,
                        })
                      }
                      style={{ width: '18rem' }}
                    >
                      <option value="">— Artikel wählen —</option>
                      {artikel.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.bezeichnung}
                        </option>
                      ))}
                    </select>
                    <Zelle
                      wert={position.menge}
                      darf={darf}
                      schritt={0.5}
                      onAendern={(w) =>
                        void aenderePosition(satz, feld, index, { menge: Number(w) })
                      }
                    />
                    <button
                      onClick={() => void aenderePosition(satz, feld, index, null)}
                      disabled={!darf}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  onClick={() =>
                    void aenderePosition(satz, feld, satz[feld].length, { artikelId: '', menge: 1 })
                  }
                  disabled={!darf}
                >
                  Position hinzufügen
                </button>
              </div>
            ))}
        </div>
      ))}

      {saetze.length === 0 && (
        <p style={{ color: 'var(--grau)', fontSize: '0.9rem' }}>
          Noch keine Materialsätze. Sie kommen mit dem Startbestand vom Server.
        </p>
      )}
    </section>
  );
}
